<template>
  <el-form :model="query" :inline="true" size="small" class="edu-filter">
    <!-- 周/月报表：签到与出货同一区间 -->
    <el-form-item v-if="mode === 'range'" label="统计区间：">
      <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd"
                      range-separator="至"
                      start-placeholder="开始日期" end-placeholder="结束日期" :picker-options="pickerOptions"
                      :clearable="false" @change="emitChange" />
    </el-form-item>
    <!-- 年度报表：年份 + 统计到几月 -->
    <template v-else>
      <el-form-item label="年份：">
        <el-date-picker v-model="yearStr" type="year" value-format="yyyy"
                        :clearable="false"
                        style="width:110px" @change="emitChange" />
      </el-form-item>
      <el-form-item label="统计到：">
        <el-select v-model="query.endMonth" style="width:90px" @change="emitChange">
          <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
        </el-select>
      </el-form-item>
    </template>

    <el-form-item label="老师：">
      <el-input v-model="query.keywords" placeholder="姓名/手机号" clearable
                style="width:150px"
                @keyup.enter.native="emitChange" />
    </el-form-item>
    <el-form-item v-if="showCustomer" label="客户：">
      <el-input v-model="query.customerKeywords" placeholder="姓名/手机号/用友编码" clearable
                style="width:170px"
                @keyup.enter.native="emitChange" />
    </el-form-item>
    <!-- 督导筛选只对全量账号有意义；督导/老师账号后端已按本人限定 -->
    <el-form-item v-if="!scope.restricted" label="督导：">
      <el-select v-model="query.supervisorId" placeholder="全部" clearable
                 filterable
                 style="width:140px" @change="emitChange">
        <el-option v-for="s in supervisorOptions" :key="s.id" :label="`${s.realName} (${s.mobile})`" :value="s.id" />
      </el-select>
    </el-form-item>
    <el-form-item v-if="showBig" label="大客户：">
      <el-select v-model="query.isBig" placeholder="全部" clearable
                 style="width:90px"
                 @change="emitChange">
        <el-option label="是" :value="true" />
        <el-option label="否" :value="false" />
      </el-select>
    </el-form-item>
    <!-- 剔除商品：本期不纳入统计的商品，按商品行零售价占比等比扣减 -->
    <el-form-item label="剔除商品：">
      <el-select v-model="query.excludeGoodsIds" multiple filterable
                 remote reserve-keyword collapse-tags
                 placeholder="输入商品名搜索，可多选" :remote-method="searchGoods" :loading="goodsLoading"
                 style="width:260px" @change="emitChange">
        <el-option v-for="g in goodsOptions" :key="g.key" :label="`${g.key} ${g.value}`" :value="g.key" />
      </el-select>
    </el-form-item>
    <el-form-item>
      <el-button type="primary" size="mini" @click="emitChange">搜索</el-button>
      <el-button type="info" size="mini" plain @click="reset">重置</el-button>
      <el-button v-if="exportCode" type="primary" size="mini"
                 plain
                 @click="$emit('export')">导出</el-button>
    </el-form-item>

    <!-- REQ-2026-823596 面板 9：画像与服务周期筛选（仅客户数据明细页开启） -->
    <el-form-item v-if="showPortrait" label="3.0画像：">
      <el-select v-model="query.portraitStatus" style="width:120px" clearable placeholder="全部" @change="emitChange">
        <el-option label="符合·有效" :value="1" />
        <el-option label="不符合" :value="2" />
        <el-option label="已失效" :value="3" />
      </el-select>
    </el-form-item>
    <el-form-item v-if="showPortrait" label="服务周期：">
      <el-select v-model="query.serviceCycleFilter" style="width:110px" clearable placeholder="全部" @change="emitChange">
        <el-option label="月度服务" :value="1" />
        <el-option label="季度服务" :value="2" />
        <el-option label="暂不服务" :value="3" />
      </el-select>
    </el-form-item>
  </el-form>
</template>

<script>
import technicianInfoApi from '@/api/technical/technicianInfo'
import sysGoodsApi from '@/api/sys/sysGoods'

/**
 * 教培数据统计公共筛选条
 * mode: 'range' 周/月区间报表；'year' 年度报表（年份 + 统计到几月）
 * 变更时 emit('change', query)，query 已是可直接发给后端的参数
 */
export default {
  name: 'EduStatsFilterBar',
  props: {
    mode: { type: String, default: 'range' },
    scope: { type: Object, default: () => ({ restricted: false }) },
    showCustomer: { type: Boolean, default: false },
    showBig: { type: Boolean, default: false },
    /**
     * 是否显示画像/服务周期筛选（REQ-2026-823596 面板 9）。
     *
     * ⚠️ 本组件被 customer / newCustomer / teacher **三个页面共享**，
     * 所以新筛选必须用开关控制，默认 false ——
     * 无条件加会让另外两个页面凭空多出两个它们不支持的筛选项，
     * 传给后端也是无效参数。
     */
    showPortrait: { type: Boolean, default: false },
    exportCode: { type: String, default: '' }
  },
  data() {
    const today = this.dayjs()
    return {
      dateRange: [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')],
      yearStr: String(this.defaultYearMonth().year),
      query: {
        keywords: '',
        customerKeywords: '',
        supervisorId: null,
        isBig: null,
        portraitStatus: null,
        serviceCycleFilter: null,
        excludeGoodsIds: [],
        endMonth: this.defaultYearMonth().endMonth
      },
      supervisorOptions: [],
      goodsOptions: [],
      goodsLoading: false,
      pickerOptions: {
        shortcuts: [
          { text: '本周', onClick: p => p.$emit('pick', this.weekRange(0)) },
          { text: '上周', onClick: p => p.$emit('pick', this.weekRange(-1)) },
          { text: '本月', onClick: p => p.$emit('pick', this.monthRange(0)) },
          { text: '上月', onClick: p => p.$emit('pick', this.monthRange(-1)) }
        ]
      }
    }
  },
  watch: {
    'scope.restricted'(v) {
      if (!v && !this.supervisorOptions.length) this.loadSupervisors()
    }
  },
  created() {
    if (!this.scope.restricted) this.loadSupervisors()
    this.searchGoods('')
  },
  methods: {
    // 需求口径：新加盟客户的业绩和签到只统计到上个月
    defaultYearMonth() {
      const today = this.dayjs()
      if (today.month() === 0) return { year: today.year() - 1, endMonth: 12 }
      return { year: today.year(), endMonth: today.month() }
    },
    weekRange(offset) {
      const monday = this.dayjs().startOf('week').add(1, 'day').add(offset, 'week')
      return [monday.toDate(), monday.add(6, 'day').toDate()]
    },
    monthRange(offset) {
      const first = this.dayjs().startOf('month').add(offset, 'month')
      return [first.toDate(), first.endOf('month').toDate()]
    },
    loadSupervisors() {
      // 督导 = technical_service_level.id 2
      technicianInfoApi.listPageForMini({ levelId: 2, status: 10, pageNo: 1, pageSize: 100 }).then(({ data }) => {
        this.supervisorOptions = (data && data.list) || []
      })
    },
    searchGoods(keyword) {
      this.goodsLoading = true
      sysGoodsApi.findOptions({ matchValue: keyword || undefined, limit: 50 }).then(({ data }) => {
        // 已选中的要保留在选项里，否则 el-select 只显示 id
        const selected = this.goodsOptions.filter(g => this.query.excludeGoodsIds.includes(g.key))
        const merged = [...selected, ...(data || [])]
        const seen = new Set()
        this.goodsOptions = merged.filter(g => !seen.has(g.key) && seen.add(g.key))
      }).finally(() => { this.goodsLoading = false })
    },
    buildQuery() {
      const q = {
        keywords: this.query.keywords || undefined,
        supervisorId: this.query.supervisorId || undefined,
        excludeGoodsIds: this.query.excludeGoodsIds.length ? this.query.excludeGoodsIds : undefined
      }
      if (this.mode === 'range') {
        q.startTime = `${this.dateRange[0]} 00:00:00`
        q.endTime = `${this.dateRange[1]} 23:59:59`
      } else {
        q.year = Number(this.yearStr)
        q.endMonth = this.query.endMonth
      }
      if (this.showCustomer) q.customerKeywords = this.query.customerKeywords || undefined
      if (this.showBig && this.query.isBig !== null && this.query.isBig !== '') q.isBig = this.query.isBig
      if (this.showPortrait) {
        q.portraitStatus = this.query.portraitStatus || undefined
        q.serviceCycleFilter = this.query.serviceCycleFilter || undefined
      }
      return q
    },
    emitChange() {
      this.$emit('change', this.buildQuery())
    },
    reset() {
      const today = this.dayjs()
      this.dateRange = [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')]
      const ym = this.defaultYearMonth()
      this.yearStr = String(ym.year)
      this.query = { keywords: '', customerKeywords: '', supervisorId: null, isBig: null, portraitStatus: null, serviceCycleFilter: null, excludeGoodsIds: [], endMonth: ym.endMonth }
      this.emitChange()
    }
  }
}
</script>

<style scoped>
.edu-filter ::v-deep .el-form-item {
  margin-bottom: 12px;
}
</style>
